import { StyleSheet, View } from 'react-native'
import { Text, useTheme } from 'react-native-paper'
import { ProgressCircle } from 'react-native-svg-charts'
import { useQuotaCheck } from '@/hooks/useUsage'
import type { QuotaType } from '@/types/billing'

interface UsageBarProps {
    type: QuotaType
    label: string
}

export function UsageBar({ type, label }: UsageBarProps) {
    const theme = useTheme()
    const { getUsage, getUsagePercent, isUnlimited } = useQuotaCheck()

    const { current, limit } = getUsage(type)
    const percent = getUsagePercent(type)
    const unlimited = isUnlimited(type)

    const getProgressColor = () => {
        if (unlimited) return theme.colors.primary
        if (percent >= 100) return theme.colors.error
        if (percent >= 80) return theme.colors.tertiary
        return theme.colors.primary
    }

    const limitText = unlimited ? `${current} / Unlimited` : `${current} / ${limit}`

    return (
        <View style={styles.container}>
            <ProgressCircle
                style={styles.circle}
                progress={unlimited ? 0.05 : percent / 100}
                progressColor={getProgressColor()}
                backgroundColor={theme.colors.surfaceVariant}
                strokeWidth={6}
                cornerRadius={0}
            />
            <Text variant="bodyMedium" style={styles.label}>
                {label}
            </Text>
            <Text variant="bodySmall" style={{ color: theme.colors.onSurfaceVariant }}>
                {limitText}
            </Text>
        </View>
    )
}

const styles = StyleSheet.create({
    container: {
        alignItems: 'center',
        flex: 1,
        minWidth: 80,
        gap: 4,
    },
    circle: {
        width: 64,
        height: 64,
    },
    label: {
        fontWeight: '500',
        textAlign: 'center',
    },
})
